/**
 * Gössl Tag-Lookup (r39) — vom Etikett zum Produkt.
 * Resolves what customers type (or scan) from a hang tag — the EAN barcode,
 * the printed Artikelnummer (5+4, optional FarbNr) or a bare group id —
 * against the gwandl-mcp /storefront/lookup endpoint:
 *   online article  → straight to the PDP, colour + size preselected
 *   POS-only article → "Filial-Stück" card on the search page with per-store
 *                      availability (no Reservieren since r186: not online)
 * Owns ALL pure-number queries (capture-phase submit listener runs before
 * smart-search.js). Exposes window.GoesslTagLookup.handleRaw for the scanner.
 */
(function () {
  'use strict';

  var LOOKUP = 'https://gwandl-mcp.g-ssl.workers.dev/storefront/lookup';
  var STAV = 'https://gwandl-mcp.g-ssl.workers.dev/storefront/store-availability';

  function esc(v) {
    return String(v == null ? '' : v).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function eanCheckOk(d) {
    var sum = 0;
    for (var i = 0; i < 12; i++) sum += Number(d[i]) * (i % 2 === 0 ? 1 : 3);
    return (10 - (sum % 10)) % 10 === Number(d[12]);
  }

  // Mirrors the server parser: digits decide the kind.
  function parse(raw) {
    var digits = String(raw || '').replace(/\D/g, '');
    if (!digits || String(raw).replace(/[\d\s._\-]/g, '') !== '') return null; // letters → not a tag number
    if (digits.length === 13) return eanCheckOk(digits) ? { kind: 'ean' } : null;
    if (digits.length === 9) return { kind: 'article' };
    if (digits.length >= 10 && digits.length <= 12) return { kind: 'article' };
    if (digits.length === 5) return { kind: 'group' };
    return null;
  }

  function storeCtx() {
    try {
      var s = JSON.parse(localStorage.getItem('g26_store') || 'null');
      return s && s.page ? s.page : '';
    } catch (e) { return ''; }
  }

  function lookup(q, src) {
    var url = LOOKUP + '?q=' + encodeURIComponent(q) + '&src=' + (src === 'scan' ? 'scan' : 'type');
    var store = storeCtx();
    if (store) url += '&store=' + encodeURIComponent(store);
    return fetch(url).then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
  }

  function optionLabel(options) {
    if (!options || !options.length) return '';
    return options.map(function (o) { return o.name + ' ' + o.value; }).join(', ');
  }

  function money(v) {
    var n = parseFloat(v);
    if (isNaN(n)) return '';
    var f = n.toFixed(2).split('.');
    return f[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + f[1] + ' €';
  }

  // The native "Keine Ergebnisse" copy is wrong once we DO have an answer.
  // Targeted by the data-search-empty marker, not by matching German copy: the count
  // line shares .g26-search__meta2, so the selector alone cannot tell them apart, and a
  // text match would stop hiding anything as soon as the storefront is translated.
  function hideNativeEmpty() {
    document.querySelectorAll('[data-search-empty]').forEach(function (el) {
      el.style.display = 'none';
    });
  }

  // ── Filial-Stück card (search page) ───────────────────────────────────────
  function renderPosCard(host, data) {
    hideNativeEmpty();
    // A typed article number carries no size intent — the arbitrary first
    // variant's Größe would mislead. Scans (EAN) keep the exact size in hand.
    var opts = data.options || [];
    if (data.kind === 'article') opts = opts.filter(function (o) { return !/größe|groesse|size/i.test(o.name); });
    var label = optionLabel(opts);
    var priceLine = '';
    if (data.priceMin && data.priceMax && parseFloat(data.priceMin) !== parseFloat(data.priceMax)) {
      priceLine = money(data.priceMin) + ' – ' + money(data.priceMax);
    } else if (data.price) {
      priceLine = money(data.price);
    }
    var card = document.createElement('div');
    card.className = 'g26-taghit g26-taghit--pos';
    card.innerHTML =
      '<div class="g26-taghit__media">' +
      (data.image ? '<img src="' + esc(data.image) + '" alt="' + esc(data.title) + '" loading="lazy">' : '') +
      '</div>' +
      '<div class="g26-taghit__body">' +
      '<p class="g26-taghit__kicker">' + window.g26t('products.tag_lookup.store_piece', 'Filial-Stück') + (data.articleNo ? ' · ' + window.g26t('products.tag_lookup.article_label', 'Artikel {{ number }}', { number: esc(data.articleNo) }) : '') + '</p>' +
      '<h3 class="g26-taghit__title">' + esc(data.title) + '</h3>' +
      (priceLine ? '<p class="g26-taghit__price">' + priceLine + (label ? ' · ' + esc(label) : '') + '</p>' : '') +
      '<div class="g26-taghit__stores" data-taghit-stores><p class="g26-taghit__loading">' + window.g26t('products.store_availability.loading', 'Verfügbarkeit wird geladen …') + '</p></div>' +
      '</div>';
    host.prepend(card);

    var vids = (data.variantIds && data.variantIds.length ? data.variantIds : [data.variantId])
      .filter(Boolean).map(function (v) { return String(v).replace(/\D/g, ''); }).filter(Boolean);
    if (!vids.length) {
      card.querySelector('[data-taghit-stores]').innerHTML =
        '<p class="g26-taghit__note">' + window.g26t('products.tag_lookup.ask_in_store', 'Fragen Sie im Geschäft Ihrer Nähe nach diesem Artikel – {{ link }}.', { link: '<a href="' + window.g26url('pages/geschaefte') + '">' + window.g26t('products.tag_lookup.all_stores', 'alle Geschäfte') + '</a>' }) + '</p>';
      return;
    }
    fetch(STAV + '?variants=' + encodeURIComponent(vids.join(',')))
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (av) {
        var box = card.querySelector('[data-taghit-stores]');
        // Felix 08.09: "nur in unseren Geschäften" + "in keinem Geschäft
        // vorrätig" widersprachen sich, und der Link zum Geschäftsverzeichnis
        // half niemandem. Der Satz zum Filial-Sortiment erscheint nur noch,
        // wenn es Bestand gibt; ohne Bestand ein ehrlicher Satz + Kundendienst.
        if (!av || !av.stores) {
          box.innerHTML = '<p class="g26-taghit__note">' + window.g26t('products.store_availability.load_error', 'Die Verfügbarkeit kann gerade nicht geladen werden – unsere Geschäfte helfen Ihnen telefonisch gerne weiter.') + '</p>';
          return;
        }
        if (!av.stores.length) {
          box.innerHTML = '<p class="g26-taghit__note">' + window.g26t('products.tag_lookup.not_available_anywhere', 'Dieses Modell ist derzeit weder online noch in einem Geschäft vorrätig.') + '</p>' +
            '<p class="g26-taghit__note">' + window.g26t('products.tag_lookup.customer_service', 'Unser Kundendienst hilft Ihnen gerne weiter: {{ contact_link }} oder {{ phone_link }}.', { contact_link: '<a href="' + window.g26url('pages/kontakt') + '">' + window.g26t('products.tag_lookup.write_message', 'Nachricht schreiben') + '</a>', phone_link: '<a href="tel:+4366246966">+43 662 46966</a>' }) + '</p>';
          return;
        }
        var pinned = storeCtx();
        av.stores.sort(function (a, b) { return (b.page === pinned) - (a.page === pinned); });
        box.innerHTML = '<p class="g26-taghit__note">' + window.g26t('products.tag_lookup.stores_only_reserve', 'Dieses Modell führen wir derzeit nur in unseren Geschäften.') + '</p>' +
          '<p class="g26-taghit__stores-label">' + window.g26t('products.tag_lookup.in_stock_at', 'Vorrätig in:') + '</p>' + av.stores.map(function (s) {
          var page = String(s.page || '').replace(/[^a-z0-9-]/g, '');
          var sameName = !s.name || s.name === s.city || s.name.indexOf(s.city) !== -1;
          var nameHtml = sameName ? '' :
            (page ? '<a class="g26-stav__name" href="' + window.g26url('pages/' + page) + '">' + esc(s.name) + '</a>' : '<span class="g26-stav__name">' + esc(s.name) + '</span>');
          var cityHtml = (page && sameName) ? '<a class="g26-stav__city" href="' + window.g26url('pages/' + page) + '">' + esc(s.city) + '</a>' : '<span class="g26-stav__city">' + esc(s.city) + '</span>';
          // r186 (#531, Felix 28.09.2026): Reservieren only in a piece's first 30 days
          // ONLINE - a Filial-Stück is not online at all, so its rows carry no reserve
          // link (the mailto path that fed it is gone with it).
          return '<div class="g26-stav__row">' +
            '<span class="g26-stav__dot" aria-hidden="true"></span>' +
            '<span class="g26-stav__store">' + cityHtml + nameHtml + '</span></div>';
        }).join('');
      })
      .catch(function () {
        var box = card.querySelector('[data-taghit-stores]');
        if (box) box.innerHTML = '<p class="g26-taghit__note">' + window.g26t('products.store_availability.load_error', 'Die Verfügbarkeit kann gerade nicht geladen werden – unsere Geschäfte helfen Ihnen telefonisch gerne weiter.') + '</p>';
      });
  }

  function renderMissCard(host, q) {
    hideNativeEmpty();
    var card = document.createElement('div');
    card.className = 'g26-taghit g26-taghit--miss';
    card.innerHTML = '<div class="g26-taghit__body"><p class="g26-taghit__kicker">' + window.g26t('products.product.article_number', 'Artikelnummer') + '</p>' +
      '<p class="g26-taghit__note">' + window.g26t('products.tag_lookup.not_found', 'Zur Nummer „{{ number }}“ haben wir leider nichts gefunden.', { number: esc(q) }) + ' ' +
      window.g26t('products.tag_lookup.check_digits', 'Prüfen Sie die Ziffernfolge vom Etikett – oder {{ link }}.', { link: '<a href="' + window.g26url('pages/kontakt') + '">' + window.g26t('products.tag_lookup.ask_us', 'fragen Sie uns direkt') + '</a>' }) + '</p></div>';
    host.prepend(card);
  }

  // Public entry: resolve a raw tag value; navigates or renders in place.
  function handleRaw(raw, src, host) {
    return lookup(raw, src).then(function (data) {
      if (!data) return false;
      if (data.type === 'online' && data.url) {
        window.location.href = (/^https?:\/\//i.test(data.url) ? data.url : window.g26url(data.url));
        return true;
      }
      if (data.type === 'pos_only') {
        if (host) { renderPosCard(host, data); return true; }
        // no host (submit intercept): carry to the search page, which re-runs the lookup
        window.location.href = window.g26url('search') + '?q=' + encodeURIComponent(raw) + '&type=product';
        return true;
      }
      if (host) { renderMissCard(host, raw); return true; }
      return false;
    });
  }

  window.GoesslTagLookup = { parse: parse, handleRaw: handleRaw, lookup: lookup };

  // ── Submit intercept (capture phase — runs before smart-search.js) ───────
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.getAttribute('role') !== 'search') return;
    var input = form.querySelector('input[name="q"]');
    if (!input) return;
    var raw = input.value.trim();
    if (!parse(raw)) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    input.setAttribute('aria-busy', 'true');
    handleRaw(raw, 'type', null).then(function (handled) {
      input.removeAttribute('aria-busy');
      if (!handled) form.submit();
    });
  }, true);

  // ── Search page: resolve ?q= tag patterns into the results area ──────────
  function onSearchPage() {
    if (window.location.pathname.indexOf(window.g26url('search')) !== 0) return;
    var q = new URLSearchParams(window.location.search).get('q') || '';
    if (!parse(q)) return;
    var host = document.querySelector('[data-tag-lookup-host]') || document.querySelector('main');
    if (!host) return;
    lookup(q, 'type').then(function (data) {
      if (!data) return;
      if (data.type === 'online' && data.url) { window.location.replace(/^https?:\/\//i.test(data.url) ? data.url : window.g26url(data.url)); return; }
      if (data.type === 'pos_only') renderPosCard(host, data);
      else renderMissCard(host, q);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', onSearchPage);
  else onSearchPage();
})();
